import React, { useState, useEffect } from 'react';
import { Home, RefreshCw, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Comparable-sales evidence for a JV submission.
 *
 * Comping runs in the background when a submission is created; this only reads
 * the stored result. Nothing here computes a valuation.
 *
 * Presentation follows one rule: every number an admin sees traces to a
 * database row. The deterministic block (ARV, AVM, comps) renders on its own,
 * and Claude's assessment is clearly separated and labelled — it comments on
 * those figures but is never their source.
 */

interface Comp {
  address: string;
  zip5: string;
  sale_price: number;
  sale_date: string;
  months_ago: number;
  sqft: number;
  beds: number | null;
  baths: number | null;
  year_built: number | null;
  distance_mi: number;
  adjusted_price: number | null;
  weight: number;
}

interface Assessment {
  verdict: 'agree' | 'worth_checking' | 'disagree' | 'insufficient_evidence';
  direction: 'understated' | 'overstated' | 'aligned' | 'unknown';
  headline: string;
  reasoning: string;
  key_comps: string[];
  caveats: string[];
  recommended_action: string;
  offer_impact: string;
}

interface Subject {
  apn?: string | null;
  taxaccountnumber?: string | null;
  county?: string | null;
  fips?: number | null;
  owner1_full?: string | null;
  owner_occupied?: boolean | null;
  property_city?: string | null;
  property_state?: string | null;
  taxamt?: number | null;
  tax_year?: string | null;
  tax_delinquent_year?: string | null;
  pfc_flag?: boolean | null;
  usps_vacancy_flag?: boolean | null;
  lien_amount?: number | null;
  mls_status?: string | null;
  mls_amount?: string | null;
}

interface Delinquency {
  years_delinquent?: number | null;
  recent_years_delinquent?: number | null;
  total_due?: number | null;
  total_past_due?: number | null;
  earliest_delinquent_year?: number | null;
  last_delinquent_year?: number | null;
  payment_plan?: boolean | null;
  foreclosure?: boolean | null;
  bankrupt?: boolean | null;
  taxdeed_applied?: boolean | null;
  taxes_sold_flag?: boolean | null;
}

interface CompBlock {
  status: string;
  arv_estimate?: number;
  arv_range?: number[];
  confidence?: 'high' | 'medium' | 'low';
  comp_count?: number;
  tier_used?: string;
  median_distance_mi?: number;
  dispersion_cod?: number;
  brackets_subject?: boolean;
  distressed_comps_dropped?: number;
  adjustment_method?: string;
  avm_estimate?: number;
  avm_delta_pct?: number;
  data_as_of?: string;
  data_lag_months?: number;
  claimed_arv?: number;
  searched_for?: {
    fips: number;
    house_number: string;
    street_name: string;
    zip5: string;
  };
  subject?: Subject;
  delinquency?: Delinquency | null;
  comps?: Comp[];
  assessment?: Assessment;
  assessment_error?: string;
  compedAt?: string;
  algorithmVersion?: string;
  reason?: string;
}

interface Props {
  submissionId: string;
  claimedArv?: string | number;
}

const money = (n?: number | null) =>
  n == null ? '—' : `$${Math.round(n).toLocaleString()}`;

const CONFIDENCE_STYLE: Record<string, string> = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-gray-200 text-gray-700',
};

const VERDICT_STYLE: Record<string, { badge: string; label: string }> = {
  agree: { badge: 'bg-green-100 text-green-800', label: 'Supported' },
  worth_checking: { badge: 'bg-yellow-100 text-yellow-800', label: 'Worth checking' },
  disagree: { badge: 'bg-red-100 text-red-800', label: 'Not supported' },
  insufficient_evidence: { badge: 'bg-gray-200 text-gray-700', label: 'Not enough evidence' },
};

// Why a comp block can exist without a usable ARV. These are ordinary outcomes,
// not errors — a thin rural market genuinely cannot be comped, and saying so is
// more useful than a number nobody should trust.
const STATUS_EXPLANATION: Record<string, string> = {
  no_parcel_match:
    'No county parcel record exactly matched this address, so this property was not comped. ' +
    'Matching is exact by design — a near-match risks valuing the wrong house. ' +
    'Check the address below against the submission, correct it if wrong, then Re-comp.',
  unparseable_address:
    'The address could not be parsed into a house number and street name. ' +
    'Check that propertyStreet starts with a street number and contains no city or state.',
  unknown_zip: 'The ZIP code is not in the county crosswalk, so no county could be identified.',
  uncompable_property_type: 'This property type (land, commercial, mobile home) is not comparable to residential sales.',
  insufficient_comps: 'Too few comparable sales nearby to produce a defensible figure.',
  insufficient_confidence: 'Comparable sales disagreed with each other too much to trust a median.',
  subject_missing_sqft: 'The county record has no building square footage for this property.',
  no_county_data: 'No sales data available for this county.',
};

export function PropertyCompsSection({ submissionId, claimedArv }: Props) {
  const [block, setBlock] = useState<CompBlock | null>(null);
  const [loading, setLoading] = useState(true);
  const [notComped, setNotComped] = useState(false);
  const [error, setError] = useState('');
  const [recomping, setRecomping] = useState(false);
  const [recompNote, setRecompNote] = useState('');

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const authHeaders = async () => {
    const session = await fetchAuthSession();
    return {
      Authorization: `Bearer ${session.tokens?.idToken?.toString()}`,
      'Content-Type': 'application/json',
    };
  };

  const load = async () => {
    setLoading(true);
    setError('');
    setNotComped(false);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/jv-submissions/${submissionId}/comps`,
        { headers: await authHeaders() }
      );
      if (res.status === 404) {
        // Expected for anything created before background comping existed.
        setNotComped(true);
        return;
      }
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setBlock(await res.json());
    } catch (err: any) {
      setError(err.message || 'Could not load comps');
    } finally {
      setLoading(false);
    }
  };

  const recomp = async () => {
    setRecomping(true);
    setRecompNote('');
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/jv-submissions/${submissionId}/comps`,
        { method: 'POST', headers: await authHeaders() }
      );
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setRecompNote('Re-comp started. Refresh in a minute to see the result.');
    } catch (err: any) {
      setRecompNote(err.message || 'Could not start re-comp');
    } finally {
      setRecomping(false);
    }
  };

  const claimed =
    block?.claimed_arv ??
    (typeof claimedArv === 'string'
      ? parseFloat(claimedArv.replace(/[^0-9.]/g, '')) || undefined
      : claimedArv);

  const header = (
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
        <Home className="w-4 h-4" /> Property &amp; Comparable Sales
      </h3>
      <button
        onClick={recomp}
        disabled={recomping}
        className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50
                   disabled:opacity-50 flex items-center gap-1"
        title="Re-run comping. Useful after correcting an address — county sales data only refreshes about every six months, so the figures are otherwise unchanged."
      >
        <RefreshCw className={`w-3 h-3 ${recomping ? 'animate-spin' : ''}`} />
        {recomping ? 'Starting…' : 'Re-comp'}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        {header}
        <p className="text-sm text-gray-500">Loading comparable sales…</p>
      </div>
    );
  }

  if (notComped) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        {header}
        <p className="text-sm text-gray-600">
          This submission has not been comped. Comping runs automatically for
          submissions created after the feature shipped; older deals were not
          backfilled.
        </p>
        {recompNote && <p className="text-xs text-gray-500 mt-2">{recompNote}</p>}
      </div>
    );
  }

  if (error || !block) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        {header}
        <p className="text-sm text-red-600">{error || 'No data'}</p>
      </div>
    );
  }

  // A comp block that exists but produced no ARV. Explain why rather than
  // showing an empty panel.
  if (block.status !== 'ok') {
    const addressProblem =
      block.status === 'no_parcel_match' ||
      block.status === 'unparseable_address' ||
      block.status === 'unknown_zip';
    return (
      <div
        className={`bg-white border rounded-lg p-4 ${
          addressProblem ? 'border-amber-300' : 'border-gray-200'
        }`}
      >
        {header}
        {/* An address problem is actionable — an admin can fix it. Distinguish
            it from a market problem (thin comps, high dispersion), which no
            amount of editing will resolve. */}
        {addressProblem && (
          <div className="mb-3 inline-flex items-center gap-1.5 px-2 py-1 rounded
                          bg-amber-100 text-amber-900 text-xs font-medium">
            <AlertTriangle className="w-3.5 h-3.5" />
            No address match — not comped
          </div>
        )}
        <div className="flex items-start gap-2 text-sm text-gray-700">
          <Info className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
          <div>
            <p>{STATUS_EXPLANATION[block.status] || `Comping returned: ${block.status}`}</p>
            {block.searched_for && (
              <div className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-200
                              rounded px-2 py-1.5 font-mono">
                searched: {block.searched_for.house_number}{' '}
                {block.searched_for.street_name} · ZIP {block.searched_for.zip5} ·
                county FIPS {block.searched_for.fips}
              </div>
            )}
            {block.dispersion_cod != null && (
              <p className="text-xs text-gray-500 mt-1">
                Comp disagreement (COD) {block.dispersion_cod} across{' '}
                {block.comp_count} sales.
              </p>
            )}
            {block.avm_estimate != null && (
              <p className="text-xs text-gray-600 mt-2">
                County AVM for this property: <strong>{money(block.avm_estimate)}</strong>
              </p>
            )}
          </div>
        </div>
        {recompNote && <p className="text-xs text-gray-500 mt-2">{recompNote}</p>}
      </div>
    );
  }

  const claimDelta =
    claimed && block.arv_estimate
      ? ((claimed - block.arv_estimate) / block.arv_estimate) * 100
      : null;

  const verdict = block.assessment
    ? VERDICT_STYLE[block.assessment.verdict] ?? VERDICT_STYLE.insufficient_evidence
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      {header}

      {/* Deterministic figures. No model involvement — every value here comes
          from county records or arithmetic over them. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <p className="text-xs text-gray-500">Comp-derived ARV</p>
          <p className="text-lg font-semibold">{money(block.arv_estimate)}</p>
          {block.arv_range && (
            <p className="text-xs text-gray-400">
              {money(block.arv_range[0])} – {money(block.arv_range[1])}
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500">County AVM</p>
          <p className="text-lg font-semibold">{money(block.avm_estimate)}</p>
          {block.avm_delta_pct != null && (
            <p className="text-xs text-gray-400">
              {block.avm_delta_pct > 0 ? '+' : ''}
              {block.avm_delta_pct}% vs comps
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500">Partner's ARV</p>
          <p className="text-lg font-semibold">{money(claimed)}</p>
          {claimDelta != null && (
            <p
              className={`text-xs font-medium ${
                Math.abs(claimDelta) > 25
                  ? 'text-red-600'
                  : Math.abs(claimDelta) > 15
                  ? 'text-yellow-700'
                  : 'text-gray-400'
              }`}
            >
              {claimDelta > 0 ? '+' : ''}
              {claimDelta.toFixed(0)}% vs comps
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500">Confidence</p>
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
              CONFIDENCE_STYLE[block.confidence || 'low']
            }`}
          >
            {block.confidence}
          </span>
          <p className="text-xs text-gray-400 mt-1">
            {block.comp_count} comps · {block.tier_used}
          </p>
        </div>
      </div>

      {/* Staleness is not a footnote: county data refreshes roughly every six
          months, so an ARV is as-of that date, not today. */}
      {block.data_as_of && (
        <p className="text-xs text-gray-500 mb-3">
          Based on county sales through <strong>{block.data_as_of}</strong>
          {block.data_lag_months != null && ` (${block.data_lag_months} months ago)`}
          {block.distressed_comps_dropped
            ? ` · ${block.distressed_comps_dropped} likely-distressed sales excluded`
            : ''}
        </p>
      )}

      {/* Parcel identity card — sourced from TaxLates Data (county parcel records) */}
      {block.subject && (block.subject.apn || block.subject.county || block.subject.owner1_full) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Property Details
            </p>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
              TaxLatesData
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
            {block.subject.apn && (
              <div>
                <span className="text-xs text-gray-400">APN</span>
                <p className="font-mono text-gray-800 text-xs">{block.subject.apn}</p>
              </div>
            )}
            {block.subject.taxaccountnumber && (
              <div>
                <span className="text-xs text-gray-400">Tax Account #</span>
                <p className="font-mono text-gray-800 text-xs">{block.subject.taxaccountnumber}</p>
              </div>
            )}
            {block.subject.fips != null && (
              <div>
                <span className="text-xs text-gray-400">FIPS</span>
                <p className="text-gray-800 text-xs">{block.subject.fips}</p>
              </div>
            )}
            {block.subject.county && (
              <div>
                <span className="text-xs text-gray-400">County</span>
                <p className="text-gray-800 text-xs">{block.subject.county}</p>
              </div>
            )}
            {block.subject.owner1_full && (
              <div>
                <span className="text-xs text-gray-400">Owner</span>
                <p className="text-gray-800 text-xs">{block.subject.owner1_full}</p>
              </div>
            )}
            {block.subject.owner_occupied != null && (
              <div>
                <span className="text-xs text-gray-400">Owner Occupied</span>
                <p className="text-gray-800 text-xs">{block.subject.owner_occupied ? 'Yes' : 'No'}</p>
              </div>
            )}
            {block.subject.taxamt != null && (
              <div>
                <span className="text-xs text-gray-400">
                  Annual Tax{block.subject.tax_year ? ` (${block.subject.tax_year})` : ''}
                </span>
                <p className="text-gray-800 text-xs">{money(block.subject.taxamt)}</p>
              </div>
            )}
            {block.subject.lien_amount != null && block.subject.lien_amount > 0 && (
              <div>
                <span className="text-xs text-gray-400">Lien Amount</span>
                <p className="text-orange-700 text-xs font-medium">{money(block.subject.lien_amount)}</p>
              </div>
            )}
            {block.subject.mls_status && (
              <div>
                <span className="text-xs text-gray-400">MLS Status</span>
                <p className="text-gray-800 text-xs">
                  {block.subject.mls_status}
                  {block.subject.mls_amount ? ` · ${block.subject.mls_amount}` : ''}
                </p>
              </div>
            )}
          </div>
          {block.data_as_of && (
              <div className="col-span-2 md:col-span-3 border-t border-gray-100 pt-1.5 mt-0.5">
                <span className="text-xs text-gray-400">Data updated at</span>
                <p className="text-gray-500 text-xs">{block.data_as_of}</p>
              </div>
            )}
          {(block.subject.pfc_flag || block.subject.usps_vacancy_flag || block.subject.tax_delinquent_year) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {block.subject.pfc_flag && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                  Pre-Foreclosure
                </span>
              )}
              {block.subject.usps_vacancy_flag && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  USPS Vacant
                </span>
              )}
              {block.subject.tax_delinquent_year && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  Tax Delinquent Since {block.subject.tax_delinquent_year}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tax delinquency card — sourced from ULK (dp_update_main), active records only */}
      {block.delinquency && block.delinquency.years_delinquent != null && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">
              Tax Delinquency
            </p>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded">
              FAM Data
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
            <div>
              <span className="text-xs text-gray-500">Years Delinquent</span>
              <p className="font-semibold text-red-700">{block.delinquency.years_delinquent}</p>
            </div>
            {block.delinquency.total_due != null && (
              <div>
                <span className="text-xs text-gray-500">Total Due</span>
                <p className="font-semibold text-red-700">{money(block.delinquency.total_due)}</p>
              </div>
            )}
            {block.delinquency.total_past_due != null && (
              <div>
                <span className="text-xs text-gray-500">Past Due</span>
                <p className="text-red-600 text-xs">{money(block.delinquency.total_past_due)}</p>
              </div>
            )}
            {block.delinquency.earliest_delinquent_year != null && (
              <div>
                <span className="text-xs text-gray-500">Earliest Delinquent Year</span>
                <p className="text-gray-800 text-xs">{block.delinquency.earliest_delinquent_year}</p>
              </div>
            )}
            {block.delinquency.last_delinquent_year != null && (
              <div>
                <span className="text-xs text-gray-500">Last Delinquent Year</span>
                <p className="text-gray-800 text-xs">{block.delinquency.last_delinquent_year}</p>
              </div>
            )}
          </div>
          {(block.delinquency.payment_plan || block.delinquency.foreclosure ||
            block.delinquency.bankrupt || block.delinquency.taxdeed_applied ||
            block.delinquency.taxes_sold_flag) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {block.delinquency.payment_plan && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  Payment Plan
                </span>
              )}
              {block.delinquency.foreclosure && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  Foreclosure
                </span>
              )}
              {block.delinquency.bankrupt && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  Bankruptcy
                </span>
              )}
              {block.delinquency.taxdeed_applied && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  Tax Deed Applied
                </span>
              )}
              {block.delinquency.taxes_sold_flag && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                  Lien Sold
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {block.comps && block.comps.length > 0 && (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-1 pr-3">Address</th>
                <th className="py-1 pr-3 text-right">Sold</th>
                <th className="py-1 pr-3">Date</th>
                <th className="py-1 pr-3 text-right">Sq Ft</th>
                <th className="py-1 pr-3">Bd/Ba</th>
                <th className="py-1 pr-3 text-right">Miles</th>
              </tr>
            </thead>
            <tbody>
              {block.comps.map((c) => {
                const cited = block.assessment?.key_comps?.some(
                  (k) => k.toUpperCase() === c.address.toUpperCase()
                );
                return (
                  <tr
                    key={`${c.address}-${c.sale_date}`}
                    className={`border-b border-gray-100 ${cited ? 'bg-blue-50' : ''}`}
                  >
                    <td className="py-1 pr-3">
                      {c.address}
                      {cited && (
                        <span className="ml-1 text-xs text-blue-600" title="Cited in the assessment">
                          ★
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-3 text-right">{money(c.sale_price)}</td>
                    <td className="py-1 pr-3">{c.sale_date?.slice(0, 10)}</td>
                    <td className="py-1 pr-3 text-right">
                      {c.sqft?.toLocaleString()}
                    </td>
                    <td className="py-1 pr-3">
                      {c.beds != null && c.baths != null
                        ? `${c.beds}/${c.baths}`
                        : '—'}
                    </td>
                    <td className="py-1 pr-3 text-right">{c.distance_mi?.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Clearly separated and labelled. Claude reasons about the figures
          above; it is never their source. */}
      {block.assessment && verdict && (
        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${verdict.badge}`}>
              {verdict.label}
            </span>
            <span className="text-xs text-gray-400">
              AI assessment · reviewed by a human before any action
            </span>
          </div>
          <p className="text-sm font-medium text-gray-900 mb-1">
            {block.assessment.headline}
          </p>
          <p className="text-sm text-gray-700 mb-2">{block.assessment.reasoning}</p>
          {block.assessment.offer_impact && (
            <p className="text-sm text-gray-700 mb-2">
              <span className="font-semibold">Offer impact:</span>{' '}
              {block.assessment.offer_impact}
            </p>
          )}
          {block.assessment.recommended_action && (
            <p className="text-sm text-gray-900 mb-2 flex items-start gap-1">
              <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
              <span>{block.assessment.recommended_action}</span>
            </p>
          )}
          {block.assessment.caveats?.length > 0 && (
            <ul className="text-xs text-gray-500 space-y-1 mt-2">
              {block.assessment.caveats.map((c, i) => (
                <li key={i} className="flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 text-yellow-600 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* A failed assessment must not look like a missing one — the comps
          above are still valid and usable. */}
      {block.assessment_error && (
        <p className="text-xs text-gray-500 border-t border-gray-200 pt-2">
          The AI assessment could not be generated for this deal. The comparable
          sales above are unaffected.
        </p>
      )}

      <p className="text-xs text-gray-400 mt-3">
        Comped {block.compedAt?.slice(0, 10)} · engine v{block.algorithmVersion} ·
        decision support only, not an appraisal
      </p>
      {recompNote && <p className="text-xs text-gray-500 mt-1">{recompNote}</p>}
    </div>
  );
}
